import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import Sorter from 'scoreboard/components/sorter'
import SortToggler from 'scoreboard/components/sort-toggler'
import icon from 'scoreboard/components/icon'

/**
 * Applies sorting capabilities to a table with headers. This view is not a
 * standlone view but instead is to be used within a view to add sorting to
 * simple tables within that view.
 *
 * @class SortableTable
 */
export default Backbone.View.extend({

	events: {
		'click th.is-sortable[data-sort-field]': 'sort',
		'touchend th.is-sortable[data-sort-field]': 'sort'
	},

	/**
	 * Initializes the view.  Options provided will be passed along to the
	 * Sorter & SortToggler components, see their documentation for the
	 * constructor options they can take.
	 *
	 * @constructs
	 * @param {Array | Fnnction} options.data The array of data to sort. Optionally can be a
	 * function to return the data to sort.  functions are called with the sortField and state
	 * parameters
	 *
	 * @param {Function} [options.defaultSort = null] a function to apply the default sort
	 *
	 * @param {String} [options.defaultSortField = null] the name of the field to use for the default sort.
	 *
	 * @param {boolean} [options.defaultSortAscending = true] if true the sort should be ascending
	 *
	 * @param {Array} options.fields An array of strings that defines the property
	 * on the data that the sort applies to.  The order of the properties should
	 * match the same order of table headers.  In the case where a header is not
	 * sortable, a null should be used at its corresponding array index. For
	 * example, given the data and table header
	 *
	 * 	{ label: 'Apple', price: 104.3, 'owned': yes }
	 *
	 * <thead>
	 * 	<th>Company</th>
	 *  <th>Owned</th>
	 *  <th>Current Price</th>
	 * </thead>
	 *
	 * If we want the headers "Company" and "Current Price" to be sortable we
	 * would set the fields array to = ['label', null, 'price']
	 *
	 * Values can also be an object with the properties "field", "period" to denote
	 * a period header that can be sorted, e.g. {field: 'score', period: 29}
	 */
	initialize: function(options = {}) {
		this.options = options
		this.data = this.options.data || []
		this.sortToggler = new SortToggler(options, this._initSortToggler.bind(this))
		this.renderHeaders()
	},

	_initSortToggler: function(state) {
		if (!_.isUndefined(this.options.periodVar)) {
			state['periodVar']= this.options.periodVar
		}
	},

	renderHeaders: function(fields = null) {
		if (fields != null) {
			this.options.fields = fields
		}

		if (_.result(this.options, 'fields.length', 0)) {
			let fields = this.options.fields
			this.$('th').each((idx, elem) => {
				if (fields.length > idx && !_.isNil(fields[idx])) {
					let element = $(elem)
					let fieldVal = fields[idx]
					let periodVal = null

					//fields can either be a simple string representing a field name or an object
					//having the properties field, period declaring the field name and period value
					//respectively
					if (!_.isString(fieldVal)) {
						periodVal = _.get(fieldVal,'period', null)
						fieldVal = fieldVal.field
					}
					if (periodVal) {
						element.attr('data-sort-period', periodVal + '')
					}

					element.attr('data-sort-field', fieldVal + '')
						.addClass('is-sortable ' + fieldVal)
				}
			})
		}
		this._applySortIndicator()
		this.disable(this.options.disabled)
	},

	/**
	 * @param {String|Event} field The name of the field to sort by, or the
	 * event object emitted from clicking a sortable header in the table.
	 * @param {boolean} [asc = null] true if the sort should be ascending, if not given
	 * sort will toggle based on its sort field's state
	 */
	sort: function(field, asc = null) {
		if (this._disabled || _.isNil(field)) {
			return
		}

		let sortField = field
		let periodVar = null
		if (!_.isString(field)) {
			// Assume field is an event
			field.stopPropagation() // Prevent anything else from happening, like zooming a dashboard. #24711
			let target = this.$(field.target)
			if (target && target.prop('tagName') != 'TH') {
				target = $(target).closest('th')
			}
			sortField = target.data('sort-field')
			periodVar = parseInt(target.data('sort-period'), 10)
		}
		if (_.isNil(sortField)) return

		this.trigger('sort', field)

		let val = {sortField: sortField}
		if (periodVar != null && !isNaN(periodVar)) {
			val.periodVar = periodVar
		}
		if (asc != null) {
			val.ascending = !!asc
		}

		this.sortToggler.toggle(val)
		this.applySort()
	},

	/**
	 * Applies the current sort state to the table, emits a "sorted" event when
	 * the array the sort applies to has finished sorting
	 *
	 * @param {Array} [data] If defined the data to sort, replacing the previous
	 * stored data, otherwise uses the stored data.
	 */
	applySort: function(data = this.data) {
		let state = this.sortToggler.state()
		let useDefault = _.isNil(state.sortField)
		if (useDefault && _.isFunction(this.options.defaultSort)) {
			this.trigger('sorted', this.options.defaultSort(state))
		} else {
			if (useDefault) {
				state.ascending = _.get(this.options, 'defaultSortAscending', true)
			}
			this.data = data
			if (state) {
				let sortData = this.data
				if (_.isFunction(sortData)) {
					sortData = sortData(state.sortField, state)
				}
				if (_.isFunction(this.options.filter)) {
					sortData = _.filter(sortData, this.options.filter)
				}

				let sorterOpts = this._prepSorterOptions(state)
				let orderedResults = new Sorter(sorterOpts).sort(sortData, this._keyExtraction.bind(this)).results
				if (_.isFunction(this.options.postSort)) {
					orderedResults = this.options.postSort(orderedResults)
				}
				this.trigger('sorted', orderedResults)

				if (!_.isFunction(this.data)) {
					this.data = orderedResults
				}
			}
		}
		this._applySortIndicator()
	},

	_applySortIndicator: function() {
		this.$('th .icon-collapsed').remove()
		let state = this.sortToggler.state()
		if (state && state.sortField) {
			//apply sort icon if any sort
			let periodMatch = ''
			if (!_.isNil(state.periodVar)) {
				periodMatch = '[data-sort-period="'+state.periodVar+'"]'
			}
			let thToSort = this.$('th[data-sort-field="'+state.sortField+'"]' + periodMatch)
			if (thToSort.length) {
				var sortIcon = $(icon('collapsed'))
					.toggleClass('is-flipped', !!state.ascending)
				let html =
					'<div class="sorted-content">' +
					`<span class="sorted-text">${_.escape(thToSort.text())}</span> ` +
					sortIcon[0].outerHTML +
					'</div>'
				thToSort.html(html).addClass('is-sorted')
			}
		}
	},

	_prepSorterOptions: function(state) {
		let field = this._getSortField(state)
		let sorterOpts = {
			alphanumeric: _.indexOf(this.options.numeric || [], field) == -1,
			reversed: !state.ascending
		}
		if (_.isFunction(this.options.applySortingOptions)) {
			sorterOpts = this.options.applySortingOptions(sorterOpts, field, state)
		}
		return sorterOpts
	},

	_keyExtraction: function(item) {
		let state = this.state()
		let field = this._getSortField(state)
		if (_.isFunction(this.options.extractSortData)) {
			return this.options.extractSortData(item, field, state)
		}
		return _.has(item, field) ? item[field] : item
	},

	_getSortField (state) {
		let field = state.sortField
		if (this.options.defaultSortField && _.isNil(field)) {
			field = this.options.defaultSortField
		}
		return field
	},

	state: function() {
		return this.sortToggler.state()
	},

	/**
	 * Set/Removes a filter function to apply before sorting, if null is passed in no filtering is done
	 */
	filter (func = null) {
		if (!func) {
			delete this.options.filter
		} else {
			this.options.filter = func
		}
		return this
	},

	/**
	 * Disables or enables sorting by clicking on the column header
	 * @param {boolean} [disable] true if the table should be marked .is-disabled
	 */
	disable(disable) {
		this._disabled = disable || false
		this.$el.toggleClass('is-disabled', this._disabled)
	}

})
